/**
 * TaskSetProfileChangeDetector
 *
 * Utility for detecting and analyzing changes between imported and existing
 * task set profiles. Extracts change detection logic for use across components.
 */

/**
 * Normalize a value for comparison - treats null, undefined, and empty string as equivalent
 * @param {any} value - Value to normalize
 * @returns {any} Normalized value
 */
export function normalizeValue(value) {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  return value;
}

/**
 * Check if two arrays are equal (order-independent)
 * @param {Array} a - First array
 * @param {Array} b - Second array
 * @returns {boolean} Whether arrays are equal
 */
export function arraysEqual(a, b) {
  if (!a && !b) return true;
  if (!a || !b) return false;
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((val, idx) => val === sortedB[idx]);
}

/**
 * Flatten hierarchical tasks into a single array
 * @param {Array} tasks - Nested task array
 * @param {Array} result - Accumulator array
 * @returns {Array} Flattened task array
 */
export function flattenTasks(tasks, result = []) {
  for (const task of tasks) {
    result.push(task);
    if (task.children?.length > 0) {
      flattenTasks(task.children, result);
    }
  }
  return result;
}

/**
 * Compare two tasks for field-level changes
 * @param {Object} imported - Imported task
 * @param {Object} existing - Existing task
 * @returns {Array} Array of change descriptions
 */
export function compareTask(imported, existing) {
  const changes = [];

  if (normalizeValue(imported.title) !== normalizeValue(existing.title)) {
    changes.push(`title changed to "${imported.title}"`);
  }
  if (normalizeValue(imported.code) !== normalizeValue(existing.code)) {
    changes.push(`code changed to "${imported.code || '(empty)'}"`);
  }
  if (!arraysEqual(imported.taskCodes || [], existing.taskCodes || [])) {
    changes.push(`task codes changed`);
  }

  return changes;
}

/**
 * Compare two subtasks for field-level changes
 * @param {Object} imported - Imported subtask
 * @param {Object} existing - Existing subtask
 * @returns {Array} Array of change descriptions
 */
export function compareSubtask(imported, existing) {
  const changes = [];

  if (normalizeValue(imported.title) !== normalizeValue(existing.title)) {
    changes.push(`title changed`);
  }
  if (normalizeValue(imported.defaultLeadByTeamId) !== normalizeValue(existing.defaultLeadByTeamId)) {
    changes.push(`lead team changed`);
  }
  if (normalizeValue(imported.weighting) !== normalizeValue(existing.weighting)) {
    changes.push(`weighting changed to ${imported.weighting}`);
  }
  if (normalizeValue(imported.itemTemplateCode) !== normalizeValue(existing.itemTemplateCode)) {
    changes.push(`template changed`);
  }

  return changes;
}

/**
 * Compare subtasks between imported and existing task
 * @param {Array} importedSubtasks - Imported subtasks
 * @param {Array} existingSubtasks - Existing subtasks
 * @param {string} taskTitle - Parent task title for context
 * @returns {Object} Subtask comparison result
 */
export function compareSubtasks(importedSubtasks, existingSubtasks, taskTitle) {
  const result = {
    added: 0,
    removed: 0,
    modified: 0,
    details: []
  };

  const existingMap = new Map(existingSubtasks.map(s => [s.versionReference, s]));
  const importedMap = new Map(importedSubtasks.map(s => [s.versionReference, s]));

  // Find added subtasks
  for (const [vr, subtask] of importedMap) {
    if (!existingMap.has(vr)) {
      result.added++;
      result.details.push(`Task "${taskTitle}": added subtask "${subtask.title}"`);
    }
  }

  // Find removed subtasks
  for (const [vr, subtask] of existingMap) {
    if (!importedMap.has(vr)) {
      result.removed++;
      result.details.push(`Task "${taskTitle}": removed subtask "${subtask.title}"`);
    }
  }

  // Find modified subtasks
  for (const [vr, importedSubtask] of importedMap) {
    const existingSubtask = existingMap.get(vr);
    if (existingSubtask) {
      const subtaskChanges = compareSubtask(importedSubtask, existingSubtask);
      if (subtaskChanges.length > 0) {
        result.modified++;
        result.details.push(
          `Task "${taskTitle}" subtask "${importedSubtask.title}": ${subtaskChanges.join(', ')}`
        );
      }
    }
  }

  return result;
}

/**
 * Detect changes between imported and existing profile
 * @param {Object} imported - Imported profile
 * @param {Object} existing - Existing profile
 * @returns {Object} Change detection result
 */
export function detectChanges(imported, existing) {
  const changes = {
    hasChanges: false,
    titleChanged: false,
    oldTitle: existing?.title || '',
    newTitle: imported.title,
    tasksAdded: 0,
    tasksRemoved: 0,
    tasksModified: 0,
    subtasksAdded: 0,
    subtasksRemoved: 0,
    subtasksModified: 0,
    details: []
  };

  // Check title change
  if (existing?.title !== imported.title) {
    changes.titleChanged = true;
    changes.hasChanges = true;
    changes.details.push(`Title changed from "${existing?.title}" to "${imported.title}"`);
  }

  // Compare task hierarchies
  const existingTasks = flattenTasks(existing?.taskProfiles || []);
  const importedTasks = flattenTasks(imported.taskProfiles || []);

  // Build maps by versionReference for comparison
  const existingTaskMap = new Map(existingTasks.map(t => [t.versionReference, t]));
  const importedTaskMap = new Map(importedTasks.map(t => [t.versionReference, t]));

  // Find added tasks (in imported but not in existing)
  for (const [vr, task] of importedTaskMap) {
    if (!existingTaskMap.has(vr)) {
      changes.tasksAdded++;
      changes.hasChanges = true;
      changes.details.push(`Added task: "${task.title}"`);
    }
  }

  // Find removed tasks (in existing but not in imported)
  for (const [vr, task] of existingTaskMap) {
    if (!importedTaskMap.has(vr)) {
      changes.tasksRemoved++;
      changes.hasChanges = true;
      changes.details.push(`Removed task: "${task.title}"`);
    }
  }

  // Find modified tasks
  for (const [vr, importedTask] of importedTaskMap) {
    const existingTask = existingTaskMap.get(vr);
    if (existingTask) {
      const taskChanges = compareTask(importedTask, existingTask);
      if (taskChanges.length > 0) {
        changes.tasksModified++;
        changes.hasChanges = true;
        changes.details.push(...taskChanges.map(c => `Task "${importedTask.title}": ${c}`));
      }

      // Compare subtasks for this task
      const subtaskChanges = compareSubtasks(
        importedTask.subtaskProfiles || [],
        existingTask.subtaskProfiles || [],
        importedTask.title
      );
      changes.subtasksAdded += subtaskChanges.added;
      changes.subtasksRemoved += subtaskChanges.removed;
      changes.subtasksModified += subtaskChanges.modified;
      if (subtaskChanges.added > 0 || subtaskChanges.removed > 0 || subtaskChanges.modified > 0) {
        changes.hasChanges = true;
      }
      changes.details.push(...subtaskChanges.details);
    }
  }

  return changes;
}

/**
 * Format change summary for display
 * @param {Object} changes - Change detection result
 * @returns {string} Formatted summary string
 */
export function formatChangeSummary(changes) {
  const parts = [];
  if (changes.titleChanged) parts.push('title');
  if (changes.tasksAdded > 0) parts.push(`+${changes.tasksAdded} tasks`);
  if (changes.tasksRemoved > 0) parts.push(`-${changes.tasksRemoved} tasks`);
  if (changes.tasksModified > 0) parts.push(`${changes.tasksModified} tasks modified`);
  if (changes.subtasksAdded > 0) parts.push(`+${changes.subtasksAdded} subtasks`);
  if (changes.subtasksRemoved > 0) parts.push(`-${changes.subtasksRemoved} subtasks`);
  if (changes.subtasksModified > 0) parts.push(`${changes.subtasksModified} subtasks modified`);
  return parts.length > 0 ? parts.join(', ') : 'No changes';
}

/**
 * Analyze profiles for changes against existing profiles
 * @param {Array} importedProfiles - Profiles from import
 * @param {Array} existingProfiles - Existing profiles in system
 * @returns {Object} Analysis result with matched, unmatched, and analyzed profiles
 */
export function analyzeProfiles(importedProfiles, existingProfiles) {
  // Find profiles that exist in the system (can potentially be updated)
  const matchedProfiles = importedProfiles.filter(profile =>
    existingProfiles.some(ep => ep.code === profile.code)
  );

  // Find profiles that don't exist (would need to be created)
  const profilesToCreate = importedProfiles.filter(profile =>
    !existingProfiles.some(ep => ep.code === profile.code)
  );

  // Analyze each matched profile for changes
  const analyzedProfiles = matchedProfiles.map(importedProfile => {
    const existing = existingProfiles.find(ep => ep.code === importedProfile.code);
    const changes = detectChanges(importedProfile, existing.profile);
    return {
      profile: importedProfile,
      existing: existing.profile,
      changes
    };
  });

  // Profiles that have actual changes
  const profilesWithChanges = analyzedProfiles.filter(item => item.changes.hasChanges);

  // Profiles that exist but have no changes
  const profilesWithoutChanges = analyzedProfiles.filter(item => !item.changes.hasChanges);

  return {
    analyzedProfiles,
    profilesWithChanges,
    profilesWithoutChanges,
    profilesToCreate
  };
}

/**
 * Change type constants for TreeGrid visualization
 */
export const CHANGE_TYPES = {
  ADDED: 'added',
  MODIFIED: 'modified',
  REMOVED: 'removed',
  UNCHANGED: 'unchanged'
};

/**
 * Get background color for change type (light version for row background)
 * @param {string} changeType - Type of change
 * @returns {string|null} CSS color value or null
 */
export function getBackgroundColor(changeType) {
  switch (changeType) {
    case CHANGE_TYPES.ADDED:
      return '#e6ffe6'; // Light green
    case CHANGE_TYPES.MODIFIED:
      return '#fff0e6'; // Light orange
    case CHANGE_TYPES.REMOVED:
      return '#ffe6e6'; // Light red
    default:
      return null;
  }
}

/**
 * Get chip color for change type (full color for chips)
 * @param {string} changeType - Type of change
 * @returns {string} CSS color value
 */
export function getChipColor(changeType) {
  switch (changeType) {
    case CHANGE_TYPES.ADDED:
      return '#8bc34a'; // Green
    case CHANGE_TYPES.MODIFIED:
      return '#ff9800'; // Orange
    case CHANGE_TYPES.REMOVED:
      return '#c70000'; // Red
    default:
      return '#3ca3f4'; // Blue
  }
}
